import { useState } from "react";
import { widgetMeta } from "@/lib/widgets";
import { useAnalytics, useAccounts } from "@/hooks/useData";
import { StatWidget } from "@/components/widgets/StatWidgets";
import { ChartWidget } from "@/components/widgets/ChartWidgets";
import { ListWidget } from "@/components/widgets/ListWidgets";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Settings2, Maximize2, X, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { RANGE_OPTIONS, rangeLabel } from "@/lib/format";

const NEEDS_ANALYTICS = (type) => {
  const meta = widgetMeta(type);
  if (meta.category === "stats" || meta.category === "charts") return true;
  return ["recent_trades", "open_positions"].includes(type);
};

function WidgetBody({ type, accountId, range, settings, shareToken, compact }) {
  const meta = widgetMeta(type);
  const needs = NEEDS_ANALYTICS(type);
  const { data, isLoading, error } = useAnalytics(needs ? accountId : null, range, shareToken);

  if (needs && isLoading) {
    return meta.category === "stats" ? (
      <div className="space-y-2">
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-3 w-20" />
      </div>
    ) : (
      <Skeleton className="h-full min-h-[100px] w-full" />
    );
  }
  if (needs && error) {
    return (
      <div className="flex h-full min-h-[64px] flex-col items-center justify-center gap-1 text-center">
        <AlertCircle className="h-4 w-4 text-[hsl(var(--warning))]" />
        <p className="text-[11px] text-muted-foreground">
          {error?.response?.status === 409 ? "Account awaiting live sync" : "Couldn't load data"}
        </p>
      </div>
    );
  }

  if (meta.category === "stats") return <StatWidget type={type} data={data} compact={compact} />;
  if (meta.category === "charts") return <ChartWidget type={type} data={data} compact={compact} />;
  return <ListWidget type={type} data={data} settings={settings} shareToken={shareToken} compact={compact} />;
}

function WidgetSettings({ item, accounts, onChange }) {
  const isComparison = item.widget_type === "account_comparison";
  const set = (patch) => onChange({ ...item.settings, ...patch });
  return (
    <PopoverContent align="end" className="w-64 space-y-3" onClick={(e) => e.stopPropagation()}>
      <p className="text-xs font-medium text-muted-foreground">Widget settings</p>
      {isComparison ? (
        <>
          {["account_a", "account_b"].map((k, idx) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs">Account {idx === 0 ? "A" : "B"}</Label>
              <Select value={item.settings?.[k] || ""} onValueChange={(v) => set({ [k]: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`widget-settings-${k}`}>
                  <SelectValue placeholder="Auto" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.account_id} value={a.account_id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </>
      ) : (
        <div className="space-y-1">
          <Label className="text-xs">Account</Label>
          <Select value={item.settings?.account_id || "__default__"}
            onValueChange={(v) => set({ account_id: v === "__default__" ? undefined : v })}>
            <SelectTrigger className="h-8 text-xs" data-testid="widget-settings-account">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__">Workspace default</SelectItem>
              {accounts.map((a) => (
                <SelectItem key={a.account_id} value={a.account_id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs">Time range</Label>
        <Select value={item.settings?.range || "__default__"}
          onValueChange={(v) => set({ range: v === "__default__" ? undefined : v })}>
          <SelectTrigger className="h-8 text-xs" data-testid="widget-settings-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">Workspace default</SelectItem>
            {RANGE_OPTIONS.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </PopoverContent>
  );
}

/**
 * The widget card chrome: title, account badge, settings, drill-down, remove.
 * Every widget supports drill-down (click or expand icon) - the consistent contract.
 */
export default function WidgetRenderer({ item, defaults, accounts = [], editMode, shareToken, onRemove, onSettingsChange, onDrill }) {
  const meta = widgetMeta(item.widget_type);
  const Icon = meta.icon;
  const accountId = item.settings?.account_id || defaults?.account_id;
  const range = item.settings?.range || defaults?.range || "all";
  const account = accounts.find((a) => a.account_id === accountId);
  const compact = item.h <= 2;
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-card text-card-foreground",
        "shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-shadow duration-150 hover:shadow-[0_10px_30px_rgba(2,6,23,0.10)]",
        editMode && "widget-editing ring-1 ring-ring/25"
      )}
      data-testid={`widget-${item.widget_type}`}
      onClick={() => {
        if (!editMode && !settingsOpen) onDrill?.(item);
      }}
    >
      <div className="flex items-start justify-between gap-2 px-3.5 pb-1 pt-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate text-xs font-medium tracking-tight">{meta.name}</span>
        </div>
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          {!shareToken && (
            <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="no-drag h-6 w-6" data-testid={`widget-settings-btn-${item.i}`}
                  onClick={(e) => e.stopPropagation()}>
                  <Settings2 className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <WidgetSettings item={item} accounts={accounts} onChange={(s) => onSettingsChange?.(item.i, s)} />
            </Popover>
          )}
          <Button variant="ghost" size="icon" className="no-drag h-6 w-6" data-testid={`widget-drill-btn-${item.i}`}
            onClick={(e) => { e.stopPropagation(); onDrill?.(item); }}>
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
          {editMode && (
            <Button variant="ghost" size="icon"
              className="no-drag h-6 w-6 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              data-testid={`widget-remove-btn-${item.i}`}
              onClick={(e) => { e.stopPropagation(); onRemove?.(item.i); }}>
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 px-3.5">
        {account && !item.settings?.account_id && null}
        {account && (
          <span className="truncate text-[10px] text-muted-foreground/70">
            {account.name} · {rangeLabel(range)}
          </span>
        )}
      </div>
      <div className="min-h-0 flex-1 px-3.5 pb-3 pt-1.5">
        <WidgetBody type={item.widget_type} accountId={accountId} range={range}
          settings={item.settings} shareToken={shareToken} compact={compact} />
      </div>
    </div>
  );
}
